import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  ExperienceAnswer,
  ExperienceAssessmentAnswer,
  ExperienceAssessmentQuestion,
  ExperienceAssessmentQuestionType,
  ProficiencyAndInterestAnswer,
} from '../../assessments/experience/experience.types';
import { useExperienceAssessmentContext } from '../../hooks/use-form-context.hooks';
import {
  ExperienceAssessmentState,
  dispatchUpdateExperienceAssessment,
} from '../../redux/slice/form-experience';
import {
  PreEmploymentAssessmentStatus,
  dispatchUpsertPreEmploymentAssessment,
} from '../../redux/slice/pre-employment-assessment';
import { useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';

export const experienceAssessmentOptions = [
  {
    label: 'Yes',
    value: 'Yes',
  },
  {
    label: 'No',
    value: 'No',
  },
];

interface FormData {
  interactedVirtually: ExperienceDetails;
  interactedInPerson: ExperienceDetails;
  resolvedQueries: ExperienceDetails;
  workedInRetail: ExperienceDetails;
  takenOrder: ExperienceDetails;
  cleanedTable: ExperienceDetails;
  servedFood: ExperienceDetails;
}

export interface ExperienceDetails {
  proficient: string;
  interested: string;
}
interface ComponentProps {
  step: number;
  questions: ExperienceAssessmentQuestion[];
  startIndex: number;
  onBack: () => void;
  completePreEmploymentOnSubmission?: boolean;
}

type AnswersState = {
  [questionId: string]: ExperienceAssessmentAnswer;
};
export const ExperienceAssessment: React.FC<ComponentProps> = ({
  step,
  onBack,
  questions,
  startIndex,
  completePreEmploymentOnSubmission,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const savedAnswers: ExperienceAssessmentState = useExperienceAssessmentContext();
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState<AnswersState>({});

  function updateAnswer(
    questionId: string,
    updatedAnswer: Partial<ExperienceAssessmentAnswer>,
  ): void {
    setError('');
    setAnswers((previousState: AnswersState) => ({
      ...previousState,
      [questionId]: {
        ...answers[questionId],
        ...updatedAnswer,
      },
    }));
  }

  function convertValueForProficiencyToString(
    answer: ExperienceAssessmentAnswer,
  ): 'Yes' | 'No' | undefined {
    const typedAnswer: ProficiencyAndInterestAnswer = answer as ProficiencyAndInterestAnswer;
    if (typedAnswer?.isProficient === undefined) {
      return undefined;
    } else if (typedAnswer.isProficient) {
      return 'Yes';
    } else {
      return 'No';
    }
  }

  function convertValueForInterestToString(
    answer: ExperienceAssessmentAnswer,
  ): 'Yes' | 'No' | undefined {
    const typedAnswer: ProficiencyAndInterestAnswer = answer as ProficiencyAndInterestAnswer;
    if (typedAnswer?.isInterested === undefined) {
      return undefined;
    } else if (typedAnswer.isInterested) {
      return 'Yes';
    } else {
      return 'No';
    }
  }

  function convertValueForExperienceToString(
    answer: ExperienceAssessmentAnswer,
  ): 'Yes' | 'No' | undefined {
    const typedAnswer: ExperienceAnswer = answer as ExperienceAnswer;
    if (typedAnswer?.hasExperience === undefined) {
      return undefined;
    } else if (typedAnswer.hasExperience) {
      return 'Yes';
    } else {
      return 'No';
    }
  }

  const { handleSubmit } = useForm<FormData>();
  const backButtonClick = () => {
    onBack();
  };
  const submitForm: SubmitHandler<FormData> = async () => {
    logger.debug('Submitted experience assessment form.', answers);

    const error_message: string = 'Please select an option from each section';
    const hasMissingAnswers: boolean = questions.some(
      (question: ExperienceAssessmentQuestion): boolean => {
        if (!answers[question.id]) {
          return true;
        }

        const answer: ExperienceAssessmentAnswer = answers[question.id];
        if (
          question.type === ExperienceAssessmentQuestionType.PROFICIENCY_AND_INTEREST &&
          (answer as ProficiencyAndInterestAnswer).isInterested === undefined
        ) {
          return true;
        } else if (
          question.type === ExperienceAssessmentQuestionType.PROFICIENCY_AND_INTEREST &&
          (answer as ProficiencyAndInterestAnswer).isProficient === undefined
        ) {
          return true;
        } else if (
          question.type === ExperienceAssessmentQuestionType.EXPERIENCE &&
          (answer as ExperienceAnswer).hasExperience === undefined
        ) {
          return true;
        }

        return false;
      },
    );
    if (hasMissingAnswers) {
      setError(error_message);
    } else {
      logger.trace('✅ Successfully submitted experience assessment form.');
      await dispatch(dispatchUpdateExperienceAssessment(answers));
      if (completePreEmploymentOnSubmission) {
        await dispatch(
          dispatchUpsertPreEmploymentAssessment({
            status: PreEmploymentAssessmentStatus.COMPLETE,
            didCompleteExperience: true,
          }),
        );
        navigate('/swot/step1');
      } else {
        navigate(`/pre-employment/experience/step${step + 1}`);
      }
    }
  };

  useEffect(() => {
    if (savedAnswers && Object.keys(savedAnswers).length) {
      setAnswers(savedAnswers);
    }
  }, [savedAnswers]);

  return (
    <>
      <div className="mt-10">
        <form
          className=" md:px-10"
          action=""
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={handleSubmit(submitForm)}
        >
          <ol style={{ listStyle: 'decimal' }} type="1" start={startIndex}>
            {questions.map((question: ExperienceAssessmentQuestion) => (
              <li className="mb-10 font-semibold" key={question.id}>
                {question.question}
                {question.type === ExperienceAssessmentQuestionType.PROFICIENCY_AND_INTEREST ? (
                  <div className="grid grid-cols-2 gap-x-8">
                    <div className="mt-5">
                      <div>
                        <p className="text-purple-600 text-sm font-semibold mb-2">
                          Are you proficient?
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="border py-3 px-6 rounded-lg flex justify-between">
                            {experienceAssessmentOptions.map((option) => (
                              <label key={option.label} className="cr-radio-container font-light">
                                {option.label}
                                <input
                                  type="radio"
                                  checked={
                                    convertValueForProficiencyToString(answers[question.id]) ===
                                    option.value
                                  }
                                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    updateAnswer(question.id, {
                                      question: question.question,
                                      isProficient: event.target.value === 'Yes',
                                    })
                                  }
                                  value={option.value}
                                  name={`proficiency-${question.id}`}
                                />
                                <span className="cr-checkmark"></span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-5">
                      <div>
                        <p className="text-purple-600 text-sm font-semibold mb-2">
                          Does this type of work interest you?
                        </p>
                        <div className="flex gap-3">
                          <div className="py-3 rounded-lg flex justify-between">
                            {experienceAssessmentOptions.map((option) => (
                              <label
                                key={option.label}
                                className="cr-radio-container mr-8 font-light"
                              >
                                {option.label}
                                <input
                                  type="radio"
                                  checked={
                                    convertValueForInterestToString(answers[question.id]) ===
                                    option.value
                                  }
                                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    updateAnswer(question.id, {
                                      question: question.question,
                                      isInterested: event.target.value === 'Yes',
                                    })
                                  }
                                  value={option.value}
                                  name={`interest-${question.id}`}
                                />
                                <span className="cr-checkmark"></span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-x-24">
                    <div className="mt-5">
                      <div>
                        <div className="flex gap-3">
                          <div className="rounded-lg flex justify-between gap-3">
                            {experienceAssessmentOptions.map((option) => (
                              <label key={option.label} className="cr-radio-container font-light">
                                {option.label}
                                <input
                                  type="radio"
                                  checked={
                                    convertValueForExperienceToString(answers[question.id]) ===
                                    option.value
                                  }
                                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    updateAnswer(question.id, {
                                      question: question.question,
                                      hasExperience: event.target.value === 'Yes',
                                    })
                                  }
                                  value={option.value}
                                  name={`experience-${question.id}`}
                                />
                                <span className="cr-checkmark"></span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-5  basis-full w-full">
                      <p className="text-purple-600 text-sm font-semibold mb-2">
                        If yes, please explain
                      </p>
                      <textarea
                        value={(answers[question.id] as ExperienceAnswer)?.comments}
                        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                          updateAnswer(question.id, {
                            question: question.question,
                            comments: event.target.value,
                          })
                        }
                        rows={2}
                        className="bg-white border rounded-lg w-full resize-none mt-4 font-light p-3"
                      ></textarea>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ol>
          {error && <p className="text-red-500 font-light !text-sm">{error}</p>}
          <div className="flex justify-between mt-20">
            <button
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={handleSubmit(backButtonClick)}
              className="btn-primary"
            >
              Back
            </button>
            <button className="btn-primary">
              {completePreEmploymentOnSubmission ? 'Submit' : 'Next'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};
